import type { ProfileCardSpan } from '@/components/common/ProfileCardGrid';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import type { PartnerProfileFormValues } from './schema';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

/**
 * The partner profile's editable sections (FR-177). The Contact button card
 * edits through `about`; the logo and cover images through the header's Edit
 * action, as in the approved mockup.
 */
export type PartnerSectionId = 'about' | 'services' | 'online' | 'images';

/** What the side panel edits: one section, or every section at once for a new profile (FR-181). */
export type PartnerEditorTarget = PartnerSectionId | 'setup';

export interface PartnerSectionConfig {
  title: string;
  /** Form fields the section's side panel edits; only these are validated and sent (FR-167). */
  fields: readonly (keyof PartnerProfileFormValues)[];
  span: ProfileCardSpan;
}

export const PARTNER_SECTIONS: Record<PartnerSectionId, PartnerSectionConfig> = {
  about: {
    title: S.ABOUT,
    fields: [
      'businessName',
      'contactName',
      'contactTitle',
      'phone',
      'websiteUrl',
      'schedulingUrl',
      'contactPreference',
      'description',
    ],
    span: 7,
  },
  services: {
    title: S.SERVICES_CARD,
    fields: ['categoryIds', 'tags', 'tagline'],
    span: 7,
  },
  online: {
    title: S.ONLINE_CARD,
    fields: ['inquiryEmail', 'linkedinUrl', 'promoVideoUrl'],
    span: 5,
  },
  images: {
    title: S.IMAGES,
    fields: ['logoFileKey', 'coverPhotoFileKey', 'listingCoverFileKey'],
    span: 12,
  },
};

/** Section order in the setup panel. */
export const PARTNER_SECTION_ORDER: readonly PartnerSectionId[] = [
  'about',
  'services',
  'online',
  'images',
];

/** The one-panel setup for a profile that does not exist yet: every section's fields. */
export const PARTNER_SETUP: PartnerSectionConfig = {
  title: L.SETUP_TITLE,
  fields: PARTNER_SECTION_ORDER.flatMap((id) => PARTNER_SECTIONS[id].fields),
  span: 12,
};

export function partnerEditorConfig(target: PartnerEditorTarget): PartnerSectionConfig {
  return target === 'setup' ? PARTNER_SETUP : PARTNER_SECTIONS[target];
}
